import { listAppRecommendations } from '@wix/ambassador-funnel-app-recommendation-v1-app-recommendation/http'
import { AppRecommendation } from '@wix/ambassador-funnel-app-recommendation-v1-app-recommendation/types'
import { Box, Button, Checkbox, Text } from '@wix/design-system'
import {
  ChoiceQuestionContentComponent,
  GroupContentComponent,
  useContent,
  useMetadata,
  useNext,
} from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import classNames from 'classnames'
import React, { FC, useEffect, useState } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { useExperiments } from '../../../hooks/useExperiments'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import {
  type App,
  AppLevel,
  FALLBACK_APPS,
  getBIAnswer,
  getBIAppInstallation,
  getSectorAppIds,
  mergeAppsData,
} from './common'
import { wixMobileAppsDataHooks as DataHooks } from './WixMobileApps.dataHooks'
import s from './WixMobileApps.scss'

export type Content = {
  builtInLabel: string
  back: string
  next: string
  skip: string
  skin?: 'harmony'
  stepProgressBar: GroupContentComponent<{ visible: boolean; total: number; current: number }>
  question: ChoiceQuestionContentComponent<{ title: string; subtitle: string }, App>
}

type ItemProps = {
  app: App
  isActive: boolean
  onClick: (appDefId: string) => void
}

const Item: FC<ItemProps> = ({ app, isActive, onClick }) => (
  <li
    className={s.WixMobileApps__item}
    onClick={() => onClick(app.appDefId)}
    data-hook={DataHooks.appItem({ key: app.optionKey })}
  >
    <div className={s.WixMobileApps__itemContent}>
      <Box className={s.WixMobileApps__itemTitle}>
        <Text weight="bold" className={classNames(s.itemTitle, { [s.itemTitleActive]: isActive })}>
          {app.name}
        </Text>
      </Box>
      <Text size="small" className={s.itemDescription}>
        {app.description}
      </Text>
    </div>
    <Checkbox checked={isActive} dataHook={DataHooks.appItemCheckbox({ key: app.optionKey })} className={s.itemCheck} />
  </li>
)

export const WixMobileApps: FC = () => {
  const [values, setValues] = useState<string[]>([])
  const [mergedApps, setMergedApps] = useState<App[]>([])
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const { content } = useContent<Content>()
  const { category, save } = useStorageContext()
  const pageLogger = usePageLogger()
  const next = useNext()
  const experiments = useExperiments()
  const { industryId, structureId, businessSegmentId } = resolveCategoryFromBusinessTerm(
    category?.businessTerm,
    category?.businessSegmentId,
  )

  const onToggle = (value: string) => {
    pageLogger.clickAnswer({
      questionKey: content.question.questionKey,
      buttonName: content.question.options.find(o => o.optionKey === value)?.biSlug || value,
    })

    if (values.includes(value)) {
      setValues(values.filter(v => v !== value))
    } else {
      setValues([...values, value])
    }
  }

  const appsSelectedBI = () => {
    const { appId, builtInAppId } = getBIAppInstallation(mergedApps, values)

    pageLogger.appsSelected({ app_id: appId, built_in_app_id: builtInAppId })
  }

  const getVisibleBIAnswer = () => getBIAnswer(mergedApps, mergedApps, values)

  const saveApps = () =>
    save({
      appDefIds: values
        .map(optionKey => mergedApps.find(a => a.optionKey === optionKey)?.appDefId)
        .filter((appDefId): appDefId is string => typeof appDefId === 'string'),
    })

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: getVisibleBIAnswer(),
    })

    appsSelectedBI()
    saveApps()
    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    pageLogger.answer({
      answer: getVisibleBIAnswer(),
    })

    appsSelectedBI()
    saveApps()
    next({ [content.question.questionKey]: { values } })
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: getVisibleBIAnswer(),
    })

    saveApps()
    window.history.back()
  }

  useEffect(() => {
    const doLoad = async () => {
      const request = listAppRecommendations({
        origin: flowBiSlug,
        structureId,
        industryId,
      })
      const [, response] = await tryCatch(() => ambassadorHttpClient.request(request))
      const recommendedApps = (response?.data.apps || FALLBACK_APPS) as (AppRecommendation & { level?: AppLevel })[]
      const merged = mergeAppsData(recommendedApps, content.question.options)

      const sectorAppIds = getSectorAppIds(businessSegmentId, experiments)
      const prechecked = merged.filter(app => sectorAppIds.includes(app.appDefId)).map(app => app.optionKey)
      const sorted = [
        ...merged.filter(app => sectorAppIds.includes(app.appDefId)),
        ...merged.filter(app => !sectorAppIds.includes(app.appDefId)),
      ]

      pageLogger.screenView({
        initialData: getBIAnswer(sorted, sorted, prechecked),
      })

      setMergedApps(sorted)
      setValues(prechecked)
    }
    void doLoad()
  }, [])

  return (
    <DesignSystemTokensLoader type="odeditor">
      <WixMobilePage>
        <div className={s.root}>
          <WixMobileHeader
            skin="harmony"
            leftContent={
              (content.back && (
                <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
              )) ||
              undefined
            }
            rightContent={
              (content.skip && (
                <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
              )) ||
              undefined
            }
          />
          <WixMobileContentWrapper dataHook={DataHooks.contentWrapper()}>
            {content.stepProgressBar.content.visible && (
              <WixMobileStepProgressBar
                current={content.stepProgressBar.content.current}
                total={content.stepProgressBar.content.total}
                skin="harmony"
                dataHook={DataHooks.stepProgressBar()}
              />
            )}
            <WixMobileHeading dataHook={DataHooks.title()} skin="harmony">
              {content.question.content.title}
            </WixMobileHeading>
            <WixMobileDescription dataHook={DataHooks.subtitle()} skin="harmony">
              {content.question.content.subtitle}
            </WixMobileDescription>
            <ul className={s.WixMobileApps}>
              {mergedApps.map(app => (
                <Item
                  key={app.appDefId}
                  app={app}
                  isActive={values.includes(app.optionKey)}
                  onClick={() => onToggle(app.optionKey)}
                />
              ))}
            </ul>
          </WixMobileContentWrapper>
          {content.next && (
            <WixMobileFooter>
              <Button
                size="large"
                fullWidth
                disabled={!values.length}
                onClick={onNextButtonClick}
                dataHook={DataHooks.next()}
                className={s.nextButton}
              >
                {content.next}
              </Button>
            </WixMobileFooter>
          )}
        </div>
      </WixMobilePage>
    </DesignSystemTokensLoader>
  )
}
